import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Sparkles, Compass, Upload, FileText, MessagesSquare, Gauge, Highlighter,
    MousePointerClick, BookMarked, Layers, ListChecks, GraduationCap, Bot,
    Target, BarChart3, Gift, CreditCard, Rocket, ArrowLeft, ArrowRight, X, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";

// Per-step icon + accent (content lives in i18n `onboarding.steps`).
const STEP_META = [
    { icon: Sparkles, accent: "174 72% 46%" },        // 1 Welcome
    { icon: Compass, accent: "206 70% 55%" },         // 2 Learning style
    { icon: Upload, accent: "150 52% 50%" },          // 3 Upload
    { icon: FileText, accent: "38 90% 58%" },         // 4 Summaries
    { icon: MessagesSquare, accent: "265 60% 65%" },  // 5 Chat
    { icon: Gauge, accent: "330 62% 64%" },           // 6 Reading modes
    { icon: Highlighter, accent: "48 90% 58%" },      // 7 Smart Highlights
    { icon: MousePointerClick, accent: "174 72% 46%" }, // 8 Click-to-Explain
    { icon: BookMarked, accent: "206 70% 55%" },      // 9 Glossary
    { icon: Layers, accent: "150 52% 50%" },          // 10 Flashcards
    { icon: ListChecks, accent: "38 90% 58%" },       // 11 Quiz
    { icon: GraduationCap, accent: "265 60% 65%" },   // 12 Exam
    { icon: Bot, accent: "330 62% 64%" },             // 13 AI Coach
    { icon: Target, accent: "10 78% 62%" },           // 14 Knowledge gaps
    { icon: BarChart3, accent: "206 70% 55%" },       // 15 Analytics
    { icon: Gift, accent: "150 52% 50%" },            // 16 Ambassador
    { icon: CreditCard, accent: "48 90% 58%" },       // 17 Pricing
    { icon: Rocket, accent: "174 72% 46%" },          // 18 Final
];

export const OnboardingTour = ({ open, onClose }) => {
    const { t } = useTranslation();
    const steps = t("onboarding.steps");
    const total = Array.isArray(steps) ? steps.length : 0;
    const [i, setI] = useState(0);

    useEffect(() => { if (open) setI(0); }, [open]);

    const isLast = i >= total - 1;
    const next = useCallback(() => { if (isLast) onClose?.(); else setI((v) => Math.min(v + 1, total - 1)); }, [isLast, onClose, total]);
    const back = useCallback(() => setI((v) => Math.max(v - 1, 0)), []);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
            else if (e.key === "ArrowRight" || e.key === "Enter") next();
            else if (e.key === "ArrowLeft") back();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, next, back, onClose]);

    if (!open || total === 0) return null;
    const step = steps[i] || {};
    const meta = STEP_META[i] || STEP_META[0];
    const Icon = meta.icon;
    const accent = `hsl(${meta.accent})`;
    const progress = ((i + 1) / total) * 100;

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[200] flex items-center justify-center p-4"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    data-testid="onboarding-tour"
                >
                    {/* Dimmed backdrop */}
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="onboarding-backdrop" />
                    {/* Spotlight glow following the step accent */}
                    <motion.div
                        key={`glow-${i}`}
                        className="pointer-events-none absolute left-1/2 top-1/2 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[100px]"
                        style={{ background: accent, opacity: 0.16 }}
                        initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 0.16 }}
                        transition={{ duration: 0.5 }}
                    />

                    <motion.div
                        className="relative flex w-full max-w-md flex-col overflow-hidden rounded-3xl border border-border bg-surface-1 shadow-elev-2"
                        initial={{ scale: 0.94, y: 16, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.96, y: 12, opacity: 0 }}
                        transition={{ type: "spring", damping: 26, stiffness: 260 }}
                        role="dialog" aria-modal="true" aria-label={step.title}
                        data-testid="onboarding-card"
                    >
                        {/* progress bar */}
                        <div className="h-1 w-full bg-surface-3">
                            <motion.div className="h-full rounded-r-full" style={{ background: accent }} animate={{ width: `${progress}%` }} transition={{ duration: 0.35 }} data-testid="onboarding-progress" />
                        </div>

                        <div className="flex items-center justify-between px-5 pt-4">
                            <span className="mono-label text-t4">{t("onboarding.badge")}</span>
                            <div className="flex items-center gap-3">
                                <span className="mono-label text-t4" data-testid="onboarding-step-counter">{t("onboarding.stepOf", { c: i + 1, total })}</span>
                                <button onClick={onClose} data-testid="onboarding-skip-x" className="text-t4 transition-colors hover:text-t1" aria-label={t("onboarding.skip")}><X className="h-4 w-4" /></button>
                            </div>
                        </div>

                        <div className="px-6 pb-2 pt-5">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: 24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -24 }}
                                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <div
                                        className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl"
                                        style={{ background: `hsl(${meta.accent} / 0.14)`, color: accent, boxShadow: `0 0 0 1px hsl(${meta.accent} / 0.25)` }}
                                        data-testid="onboarding-icon"
                                    >
                                        <Icon className="h-8 w-8" />
                                    </div>
                                    <h2 className="font-sans-display text-2xl font-semibold tracking-tight text-t1" data-testid="onboarding-title">{step.title}</h2>
                                    <p className="mt-2 text-base leading-relaxed text-t3" data-testid="onboarding-body">{step.body}</p>
                                    {Array.isArray(step.chips) && step.chips.length > 0 && (
                                        <div className="mt-4 flex flex-wrap gap-2" data-testid="onboarding-chips">
                                            {step.chips.map((c, idx) => (
                                                <span key={idx} className="rounded-full border border-border bg-surface-2 px-3 py-1.5 text-xs text-t2">{c}</span>
                                            ))}
                                        </div>
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-4">
                            <Button variant="ghost" size="sm" className="text-t4 hover:text-t2" onClick={onClose} data-testid="onboarding-skip-button">{t("onboarding.skip")}</Button>
                            <div className="flex items-center gap-2">
                                {i > 0 && (
                                    <Button variant="secondary" size="sm" className="gap-1.5 bg-surface-3/60" onClick={back} data-testid="onboarding-back-button">
                                        <ArrowLeft className="h-4 w-4" /> {t("onboarding.back")}
                                    </Button>
                                )}
                                <Button size="sm" className="gap-1.5" onClick={next} data-testid={isLast ? "onboarding-finish-button" : "onboarding-next-button"}>
                                    {isLast ? (<><Check className="h-4 w-4" /> {t("onboarding.finish")}</>) : (<>{t("onboarding.next")} <ArrowRight className="h-4 w-4" /></>)}
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
};
